import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryCityMarketingPage,
  buildIndustryCityMarketingHead,
  type IndustryCityMarketingConfig,
} from "@/components/IndustryCityMarketingPage";
import { COLORADO_SPRINGS_PROFILE } from "@/lib/cityProfiles/colorado-springs";

const CONFIG: IndustryCityMarketingConfig = {
  ...COLORADO_SPRINGS_PROFILE,
  "slug": "landscaping-marketing-colorado-springs",
  "industry": "Landscaping",
  "industryLower": "landscaping",
  "metaTitle": "Landscaping Marketing in Colorado Springs, CO | SEO, Maps, Ads & Web",
  "metaDescription": "Owner-led landscaping marketing for Colorado Springs landscapers: SEO, Google Maps, web design, and ads across Briargate and Northgate. Flat $1,650/mo, no contracts.",
  "h1": {
    "lead": "Landscaping marketing in",
    "gold": "Colorado Springs",
    "trail": "\u2014 from Briargate to Old Colorado City"
  },
  "eyebrow": "Colorado Springs Landscaping Marketing",
  "heroDescription": "We help Colorado Springs landscapers show up in Google Maps across 80920, 80921, and 80908, win 'landscaper near me' and 'xeriscaping' searches, and book the jobs worth doing before spring hits.",
  "intro": "Colorado Springs homeowners start searching for landscapers in late winter, and by April the good crews are booked out. The landscapers who win are the ones already showing up on Google Maps in Briargate, Northgate, Black Forest, with a website that shows real local work and makes it easy to request a quote. We build that: Google Maps, city pages for every area you serve, project photo galleries, and seasonal ads for design-build, xeriscaping, sod, irrigation, and maintenance. Owner-led, flat $1,650/month, no contracts.",
  "painPoints": [
    "Your spring schedule fills with small mowing jobs while bigger design-build and hardscape projects go to someone else.",
    "Homeowners can't find your past work, so they pick the landscaper with more photos and reviews.",
    "Paying lead services for shared leads that four other landscapers are calling at the same time."
  ],
  "services": {
    "seo": "Rank for 'landscaper Colorado Springs', 'xeriscaping', 'sod installation', and 'sprinkler repair' across 80920, 80921, and 80908.",
    "googleMaps": "Get into the top 3 on Google Maps in Colorado Springs with the right categories, service areas, project photos, and steady reviews.",
    "webDesign": "Fast, phone-friendly landscaping sites with project galleries and a simple quote request form.",
    "ppc": "Seasonal Google Ads and Local Services Ads that start before spring demand and pause when your crews are full.",
    "geoAi": "Get mentioned when Colorado Springs homeowners ask ChatGPT or Google AI for a landscaper they can trust.",
    "socialMedia": "Before-and-after project posts and seasonal yard tips that keep past customers calling you back."
  },
  "trustPoints": [
    "Owner-led from our Colorado Springs office. We know the Colorado Springs market.",
    "Serving Colorado businesses since 2006.",
    "Flat $1,650/month marketing, websites from $1,850, published publicly.",
    "Talk to Chris directly. No account managers or offshore handoffs."
  ],
  "faqs": [
    {
      "q": "How do I get more landscaping leads in Colorado Springs?",
      "a": "Show up in the top 3 on Google Maps for 'landscaper near me' across 80920, 80921, and 80908, add a city page for every area you serve, post real project photos, and ask every happy customer for a Google review. Seasonal ads can fill gaps while those build."
    },
    {
      "q": "How much does landscaping marketing cost in Colorado Springs?",
      "a": "Our Growth Marketing package (SEO, Google Maps, AI search, and social) is $1,650/month flat. A new landscaping website starts at $1,850 one-time, and you own it."
    },
    {
      "q": "When should landscapers start marketing for spring?",
      "a": "January and February. Homeowners start planning before the snow melts, and Google rankings take time to build, so the work you do in winter pays off in April and May."
    },
    {
      "q": "Do you cover Monument, Fountain, and Falcon too?",
      "a": "Yes. We build service-area pages and Google Maps signals for every area your crews reach, including Monument, Fountain, Falcon."
    },
    {
      "q": "Do you lock me into a contract?",
      "a": "No. Every engagement is month-to-month with no early-termination fees."
    }
  ],
  "related": [
    {
      "to": "/landscaping-marketing-colorado",
      "label": "Landscaping marketing across Colorado",
      "description": "Statewide hub for Colorado landscapers."
    },
    {
      "to": "/colorado-springs",
      "label": "Colorado Springs digital marketing hub",
      "description": "Every service we offer for Colorado Springs businesses."
    },
    {
      "to": "/landscaping-marketing-castle-rock",
      "label": "Landscaping marketing in Castle Rock",
      "description": "Same playbook tuned for Castle Rock landscapers."
    },
    {
      "to": "/landscaping-marketing-parker",
      "label": "Landscaping marketing in Parker",
      "description": "Same playbook tuned for Parker landscapers."
    },
    {
      "to": "/hvac-marketing-colorado-springs",
      "label": "HVAC marketing in Colorado Springs",
      "description": "Our HVAC playbook for Colorado Springs."
    },
    {
      "to": "/pricing",
      "label": "Flat, public pricing",
      "description": "$1,650/mo marketing, websites from $1,850."
    }
  ],
  showPricing: true,
};

export const Route = createFileRoute("/landscaping-marketing-colorado-springs")({
  head: () => buildIndustryCityMarketingHead(CONFIG),
  component: () => <IndustryCityMarketingPage config={CONFIG} />,
});
